import { gsap, ScrollTrigger } from "@/composeables/gsap"

/* Scroll Stage — scrollgesteuerte WebP-Frame-Sequenz auf Canvas (Kino-Bühne).
   Frames + Poster liegen als statische Assets unter data-frames-path
   (Craft-Feld "Scroll-Film Pfad"), Anzahl via data-frame-count.
   Frames laden erst nach window.load (Poster trägt den LCP), grob → fein.

   Portiert aus dem Standalone-Produktionspaket (HANDOFF im scrollfilm-Ordner);
   statt position:sticky + 300vh-Scroller wird hier das Projekt-Idiom
   ScrollTrigger-Pin genutzt (Pin über 200% zusätzliche Scrollstrecke). */

const SCRUB = 0.35            // Scroll-Nachlauf in Sekunden
const FILM_DISTANCE_VH = 200  // Standard-Scroll-Distanz des Films in vh (per Feld „Film-Strecke" pro Block übersteuerbar)
const ITEM_DISTANCE_VH = 70   // Scroll-Distanz pro Pin-Inhalt in vh (unabhängig vom Film-Tempo)
const KEN_BURNS_SCALE = 1.02  // Ziel-Zoom des stehenden Bildes über die gesamte Gruppendauer
const KEN_BURNS_RETURN_VH = 15 // Rückkehr zu Scale 1, überlappend mit dem folgenden Segment
const BAND_ANCHOR_PHONE = 0.22 // vertikale Lage des Kino-Bands auf Phones (0 = oben, 0.5 = mittig) — Texte nutzen die Zone darunter
const END_FADE_VH = 40        // „Ende mit erstem Frame": Überblend-Strecke in vh
const END_HOLD_VH = 30        // … und wie lange das Ausgangsbild danach stehen bleibt
const START_MIN_FRAMES = 16   // ab so vielen geladenen Frames startet das Canvas
const MAX_TRIES = 3          // Ladeversuche pro Frame
const CONCURRENCY = 6
const OVERLAY_FADE = 0.045   // Fade-Fenster der Overlays (Anteil der Strecke)

/* Zoom-Grenze für schmale (Portrait-)Screens: visW = Anteil der Frame-Breite,
   der mindestens sichtbar bleiben muss, interpoliert über den Fortschritt p.
   Hält das Motiv im Bild (beide Ärzte am Anfang, Gesicht am Ende) statt hart
   auf Cover zu croppen; Ränder werden als Kino-Band gefüllt. Auf 16:9-Screens
   greift die Grenze nie — Desktop bleibt reines Cover.
   Achtung: visW bei p:0 muss zum Crop des poster_m.webp passen! */
const FRAMING = [
    { p: 0.00, visW: 0.56 },
    { p: 0.35, visW: 0.56 },
    { p: 0.60, visW: 0.44 },
    { p: 1.00, visW: 0.44 },
]

const BG_FILL = "#050505"

function visWAt(p) {
    if (p <= FRAMING[0].p) return FRAMING[0].visW
    for (let i = 1; i < FRAMING.length; i++) {
        if (p <= FRAMING[i].p) {
            const a = FRAMING[i - 1]
            const b = FRAMING[i]
            const t = (p - a.p) / (b.p - a.p)
            return a.visW + (b.visW - a.visW) * t
        }
    }
    return FRAMING[FRAMING.length - 1].visW
}

function createScrollStage(section) {
    const qs = gsap.utils.selector(section)
    const stage = qs(".section-scroll-stage__stage")[0]
    const canvas = qs(".section-scroll-stage__canvas")[0]
    const framesPathRaw = section.dataset.framesPath
    const frameCount = parseInt(section.dataset.frameCount, 10) || 0

    if (!stage || !canvas || !framesPathRaw || frameCount < 2) return

    // data-frames-path zeigt auf .../frames/ — ältere (Blitz-gecachte)
    // Templates lieferten noch .../frames/1920/, das normalisieren wir weg
    const framesBase = framesPathRaw.replace(/1920\/?$/, "")

    /* alpha:true ist hier bewusst gewählt (vorher false): Bei alpha:false ist ein
       LEERES Canvas opak SCHWARZ. Jede Situation, in der nichts gezeichnet werden
       konnte — Frame noch nicht dekodiert, Backing-Store von resize() geleert, vom
       Browser unter Speicherdruck verworfen — legte damit eine schwarze Fläche über
       das Poster ("Bild fadet weg, Bühne schwarz"). Mit alpha:true ist ein leeres
       Canvas transparent und das Poster darunter scheint automatisch durch. Das
       Kino-Band bleibt schwarz, weil es unten explizit mit BG_FILL gefüllt wird. */
    const ctx = canvas.getContext("2d", { alpha: true })
    const loadingLabel = section.dataset.loadingLabel || "Film lädt …"
    // Phone-Layout: Kino-Band oben verankern, Texte in der Zone darunter
    // (bei Resize in onResize() nachgeführt).
    let phoneLayout = (stage.clientWidth || window.innerWidth) < 768
    const hint = qs(".section-scroll-stage__hint")[0]
    const mini = qs(".section-scroll-stage__miniload")[0]
    const readout = qs(".section-scroll-stage__readout")[0]
    const dim = qs(".section-scroll-stage__dim")[0]
    const overlays = qs(".section-scroll-stage__overlay")

    const reduceMotion = window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches

    const images = new Array(frameCount)
    const loadedF = new Uint8Array(frameCount)
    const tries = new Uint8Array(frameCount)
    let loadedCount = 0
    let settled = 0 // erfolgreich ODER endgültig aufgegeben — nur für die Anzeige
    let started = false
    let lastKey = ""
    let endFade = 0 // 0..1: Überblendung auf Frame 1 am Ende („Ende mit erstem Frame")
    const playhead = { frame: 0 }

    /* Set-Wahl: das 2560er-Set nur laden, wenn das Display es wirklich
       zeichnet. needed = benötigte Quellbreite im "schärfsten" Moment:
       Landscape zeichnet Cover (Höhe × 16/9), Portrait maximal
       Canvas-Breite ÷ kleinste Zoom-Grenze (FRAMING). Einmalig beim Init —
       ein Besucher lädt immer genau EIN Set. */
    const dprCap = Math.min(window.devicePixelRatio || 1, 2)
    const cw0 = (canvas.clientWidth || window.innerWidth) * dprCap
    const ch0 = (canvas.clientHeight || window.innerHeight) * dprCap
    const minVisW = Math.min(...FRAMING.map((f) => f.visW))
    const neededWidth = Math.min(ch0 * (16 / 9), cw0 / minVisW)
    let frameSet = neededWidth > 2200 ? "2560/" : "1920/"

    // Frame-Format aus dem CMS-Feld „Frame-Format" (data-frame-format): avif oder
    // webp. Der Redakteur pflegt es passend zum hochgeladenen Ordner — kein
    // Auto-Detect/Probe-Request nötig, das Skript weiß sofort Bescheid.
    const frameExt = section.dataset.frameFormat === "avif" ? ".avif" : ".webp"

    const srcFor = (i) => framesBase + frameSet + "f" + String(i + 1).padStart(3, "0") + frameExt

    /* Ladereihenfolge: jeder 8. → 4. → 2. → alle (Scrubben ist früh möglich) */
    const order = (() => {
        const seen = new Uint8Array(frameCount)
        const o = []
        for (const step of [8, 4, 2, 1]) {
            for (let i = 0; i < frameCount; i += step) {
                if (!seen[i]) { seen[i] = 1; o.push(i) }
            }
            if (!seen[frameCount - 1]) { seen[frameCount - 1] = 1; o.push(frameCount - 1) }
        }
        return o
    })()
    let pointer = 0
    let active = 0

    function pump() {
        while (active < CONCURRENCY && pointer < order.length) {
            load(order[pointer++])
        }
    }

    /* Fehler zählen NICHT als geladen: loadedF bleibt 0, nearestLoaded() weicht
       auf echte Nachbarn aus und der Canvas-Start wartet auf dekodierbare
       Frames — schlägt alles fehl, bleibt das Poster stehen. */
    function load(i) {
        active++
        tries[i]++
        const img = new Image()
        // Frames als Low-Priority: sie sollen später entdeckte Ressourcen
        // (Tracking, Fonts, weitere Bilder) nicht verdrängen. In Safari <17.2
        // still ignoriert — kein Fallback nötig.
        img.fetchPriority = "low"
        img.decoding = "async"
        img.onload = () => {
            active--; loadedF[i] = 1; loadedCount++; settled++
            if (img.decode) img.decode().catch(() => {})
            onProgress()
            pump()
        }
        img.onerror = () => {
            active--
            if (tries[i] < MAX_TRIES) {
                setTimeout(() => load(i), 800 * tries[i])
            } else {
                settled++
                onProgress()
            }
            pump()
        }
        img.src = srcFor(i)
        images[i] = img
    }

    function onProgress() {
        const pct = Math.round((settled / frameCount) * 100)
        if (mini) {
            if (pct < 100) mini.textContent = `${loadingLabel} ${pct}%`
            else { mini.textContent = ""; mini.style.opacity = "0" }
        }
        if (!started && loadedCount >= Math.min(START_MIN_FRAMES, frameCount)) startCanvas()
        // Bild verfeinert sich beim Nachladen — und holt das Einblenden nach, falls
        // der erste Draw in startCanvas() noch nichts zeichnen konnte.
        else if (started && draw(playhead.frame, true)) revealCanvas()
    }

    function nearestLoaded(f) {
        let idx = Math.round(f)
        if (idx < 0) idx = 0
        if (idx >= frameCount) idx = frameCount - 1
        if (loadedF[idx]) return idx
        for (let d = 1; d < frameCount; d++) {
            if (idx - d >= 0 && loadedF[idx - d]) return idx - d
            if (idx + d < frameCount && loadedF[idx + d]) return idx + d
        }
        return -1
    }

    function frameRect(img, p) {
        const cw = canvas.width
        const ch = canvas.height
        const iw = img.naturalWidth
        const ih = img.naturalHeight
        let s = Math.max(cw / iw, ch / ih)      // object-fit: cover
        const cap = cw / (visWAt(p) * iw)       // Zoom-Grenze, s. FRAMING
        if (cap < s) s = cap
        // Kino-Band: mittig — auf Phones oben verankert (Texte unten)
        let y = (ch - ih * s) / 2
        if (phoneLayout && y > 0) y = (ch - ih * s) * BAND_ANCHOR_PHONE
        return [(cw - iw * s) / 2, y, iw * s, ih * s]
    }

    /* Rückgabe: true = auf dem Canvas liegt ein gültiger Frame (gerade gezeichnet
       ODER unverändert schon vorhanden), false = es konnte nichts gezeichnet werden.
       Wird gebraucht, damit das Canvas nur eingeblendet wird, wenn es wirklich ein
       Bild zeigt — ein leeres Canvas ist wegen alpha:false opak SCHWARZ und würde
       das Poster verdecken (sah aus, als würde das Startbild wegfaden). */
    function draw(f, force) {
        const idx = nearestLoaded(f)
        if (idx < 0) return false
        const img = images[idx]
        if (!img || !img.naturalWidth) return false
        let p = f / (frameCount - 1)
        if (p < 0) p = 0
        else if (p > 1) p = 1
        const r = frameRect(img, p)
        // Neu zeichnen, wenn Frame, Framing oder End-Überblendung sich ändert
        const key = `${idx}|${Math.round(r[3])}|${Math.round(endFade * 100)}`
        if (key === lastKey && !force) return true
        lastKey = key
        /* Kino-Band NUR an den Rändern füllen, nicht das ganze Canvas. Vorher wurde
           erst die komplette Fläche mit BG_FILL geschwärzt und das Bild darüber
           gezeichnet — trug drawImage danach nichts Sichtbares bei, blieb ein
           komplett schwarzes Canvas zurück, während draw() Erfolg meldete. Auf
           Mobile-Portrait griff dieser Zweig immer (visW 0.56 -> Bild füllt die Höhe
           nicht), darum war dort genau das Muster zu sehen: Poster da, Canvas fadet
           ein, Bühne schwarz. Jetzt bleibt die Bildfläche im Fehlerfall transparent
           und das Poster darunter scheint durch. */
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        const [ix, iy, iw2, ih2] = r
        if (r[0] > 0.5 || r[1] > 0.5) {
            ctx.fillStyle = BG_FILL
            const top = Math.ceil(iy)
            const bottom = Math.floor(iy + ih2)
            const left = Math.ceil(ix)
            const right = Math.floor(ix + iw2)
            if (top > 0) ctx.fillRect(0, 0, canvas.width, top)
            if (bottom < canvas.height) ctx.fillRect(0, bottom, canvas.width, canvas.height - bottom)
            if (left > 0) ctx.fillRect(0, 0, left, canvas.height)
            if (right < canvas.width) ctx.fillRect(right, 0, canvas.width - right, canvas.height)
        }
        ctx.drawImage(img, r[0], r[1], r[2], r[3])
        // „Ende mit erstem Frame": Ausgangsbild im selben Rahmen einblenden
        if (endFade > 0) {
            const first = images[0]
            if (first && first.naturalWidth) {
                ctx.globalAlpha = endFade
                ctx.drawImage(first, r[0], r[1], r[2], r[3])
                ctx.globalAlpha = 1
            }
        }
        if (readout) readout.textContent = `Frame ${idx + 1} / ${frameCount}`
        return true
    }

    function resize() {
        const dpr = Math.min(window.devicePixelRatio || 1, 2)
        // Backing-Store nach der CSS-Box, NICHT window.inner*: iOS Safari meldet
        // beim Toolbar-Kollaps eine größere innerHeight, die 100svh-Box bleibt
        const bw = Math.round((canvas.clientWidth || window.innerWidth) * dpr)
        const bh = Math.round((canvas.clientHeight || window.innerHeight) * dpr)
        if (canvas.width !== bw || canvas.height !== bh) {
            canvas.width = bw
            canvas.height = bh
            ctx.imageSmoothingEnabled = true
            ctx.imageSmoothingQuality = "high"
            lastKey = ""
        }
        // Nach dem Leeren des Backing-Stores: gelingt der Redraw nicht, Canvas
        // ausblenden, damit das Poster durchscheint statt einer schwarzen Fläche.
        if (draw(playhead.frame, true)) { if (started) revealCanvas() }
        else if (started) hideCanvas()
    }

    // Beim Resize skaliert der Browser den alten Canvas-Bitmap kurzzeitig auf
    // die neue CSS-Box → sichtbar gestauchtes Bild, bis resize() den Backing-
    // Store neu setzt und neu zeichnet. Ein ResizeObserver läuft synchron nach
    // dem Layout (vor dem Paint) und aktualisiert Backing-Store + Frame sofort,
    // sodass kein verzerrter Zwischenframe erscheint. phoneLayout mitführen,
    // da sich die Bandverankerung an der Breite entscheidet.
    let resizeScheduled = false
    function onResize() {
        phoneLayout = (stage.clientWidth || window.innerWidth) < 768
        if (resizeScheduled) return
        resizeScheduled = true
        requestAnimationFrame(() => {
            resizeScheduled = false
            resize()
        })
    }

    /* Canvas erst einblenden, wenn wirklich ein Frame drauf liegt. Vorher wurde
       `is-visible` bedingungslos gesetzt: schlug das Zeichnen fehl (Frame noch nicht
       dekodiert, 404, Backing-Store gerade von resize() geleert), legte sich ein
       opak schwarzes Canvas über das Poster — sichtbar als „Startbild fadet weg".
       Solange nichts gezeichnet werden konnte, bleibt das Poster einfach stehen. */
    function revealCanvas() {
        if (canvas.classList.contains("is-visible")) return
        canvas.classList.add("is-visible")
    }

    /* Gegenstück: konnte nichts gezeichnet werden, obwohl das Canvas schon sichtbar
       war (z. B. resize() hat den Backing-Store geleert und der Frame ist gerade
       nicht verfügbar), lieber zurück aufs Poster als eine schwarze Fläche zeigen. */
    function hideCanvas() {
        canvas.classList.remove("is-visible")
    }

    function startCanvas() {
        started = true
        resize()
        if (draw(0, true)) revealCanvas()
    }

    /* Pin-Gruppen aus dem DOM: Der Film hält am Halte-Frame an, die Inhalte
       der Gruppe laufen durch, danach läuft der Film weiter. Die Timeline
       wird dafür segmentiert: Film-Tweens wechseln sich mit Halte-Fenstern
       ab; die Pin-Distanz wächst automatisch pro Inhalt mit. */
    const pinGroups = qs(".section-scroll-stage__pin-group")
        .map((el) => ({
            frame: Math.min(Math.max(parseInt(el.dataset.frame, 10) || 1, 1), frameCount) - 1,
            mode: el.dataset.mode === "scroll" ? "scroll" : "fade",
            kenBurns: el.dataset.kenBurns !== "false",
            items: gsap.utils.toArray(el.querySelectorAll(".section-scroll-stage__pin-item")),
        }))
        .filter((g) => g.items.length)
        .sort((a, b) => a.frame - b.frame)

    /* Timeline-Einheiten = vh: Film-Tempo (Feld „Film-Strecke") und
       Halte-Strecken pro Inhalt sind unabhängig voneinander */
    const filmDistance = parseInt(section.dataset.filmDistance, 10) || FILM_DISTANCE_VH
    const endFirstFrame = section.dataset.endFirstFrame === "true"
    const totalItems = pinGroups.reduce((n, g) => n + g.items.length, 0)
    const totalDur = filmDistance + totalItems * ITEM_DISTANCE_VH
        + (endFirstFrame ? END_FADE_VH + END_HOLD_VH : 0)

    /* Timeline + Pin sofort aufsetzen (stabile Layout-Höhe, Overlays laufen
       schon über dem Poster); nur das Canvas wartet auf geladene Frames. */
    const timeline = gsap.timeline({ defaults: { ease: "none" } })
    // Beim Scrubben ebenfalls nachziehen: war das Canvas mangels zeichenbarem Frame
    // ausgeblendet, wird es sichtbar, sobald wieder einer verfügbar ist.
    const filmTween = { onUpdate: () => { if (draw(playhead.frame) && started) revealCanvas() } }

    const buildHold = (group, at, dur) => {
        const per = dur / group.items.length
        group.items.forEach((item, i) => {
            const t0 = at + i * per
            // Phone-Layout: Items liegen per CSS volle Breite unten — kein Zentrier-Shift
            gsap.set(item, { autoAlpha: 0, xPercent: (!phoneLayout && item.dataset.pos === "center") ? -50 : 0 })
            if (group.mode === "scroll") {
                // Inhalt schiebt sich von unten nach oben über den stehenden Film
                timeline.set(item, { autoAlpha: 1 }, t0)
                timeline.fromTo(item,
                    { y: () => window.innerHeight * 0.6 },
                    { y: () => -window.innerHeight * 0.6, duration: per },
                    t0)
                timeline.set(item, { autoAlpha: 0 }, t0 + per)
            } else {
                // Inhalte nacheinander ein-/ausblenden (wie Overlays)
                const F = Math.min(OVERLAY_FADE * totalDur, per * 0.25)
                timeline.fromTo(item,
                    { autoAlpha: 0, y: 26 },
                    { autoAlpha: 1, y: 0, duration: F },
                    t0)
                timeline.to(item, { autoAlpha: 0, y: -26, duration: F }, t0 + per - F)
            }
        })
        if (group.kenBurns && !reduceMotion) {
            // EIN durchgehender, langsamer Zoom über die gesamte Gruppendauer
            // (fromTo mit expliziten Werten: deterministisch, kein Start-Einbrennen)
            timeline.fromTo(canvas,
                { scale: 1 },
                { scale: KEN_BURNS_SCALE, duration: dur, ease: "none", immediateRender: false },
                at)
            // Rückkehr zu 1 überlappend mit dem folgenden Segment — die dortige
            // Filmbewegung maskiert den Rück-Zoom. Nur wenn danach noch Strecke ist.
            const restdauer = totalDur - (at + dur)
            if (restdauer > 1) {
                timeline.fromTo(canvas,
                    { scale: KEN_BURNS_SCALE },
                    { scale: 1, duration: Math.min(KEN_BURNS_RETURN_VH, restdauer), ease: "sine.out", immediateRender: false },
                    at + dur)
            }
        }
    }

    /* Film in Segmente schneiden, Halte-Fenster einfügen (Einheiten: vh) */
    let cursor = 0
    let prevFrame = 0
    for (const g of pinGroups) {
        if (g.frame > prevFrame) {
            const segDur = ((g.frame - prevFrame) / (frameCount - 1)) * filmDistance
            timeline.to(playhead, { frame: g.frame, duration: segDur, ...filmTween }, cursor)
            cursor += segDur
        }
        const holdDur = g.items.length * ITEM_DISTANCE_VH
        buildHold(g, cursor, holdDur)
        cursor += holdDur
        prevFrame = Math.max(prevFrame, g.frame)
    }
    if (prevFrame < frameCount - 1) {
        const segDur = ((frameCount - 1 - prevFrame) / (frameCount - 1)) * filmDistance
        timeline.to(playhead, { frame: frameCount - 1, duration: segDur, ...filmTween }, cursor)
        cursor += segDur
    }

    /* „Ende mit erstem Frame": Ausgangsbild über den letzten Frame einblenden,
       danach stehen lassen (END_HOLD_VH) bis die Sektion entpinnt */
    if (endFirstFrame) {
        const fade = { v: 0 }
        timeline.to(fade, {
            v: 1,
            duration: END_FADE_VH,
            onUpdate: () => { endFade = fade.v; draw(playhead.frame, true) },
        }, cursor)
    }

    if (dim) timeline.to(dim, { opacity: 1, duration: 0.07 * totalDur }, 0.93 * totalDur)

    /* Overlay-Fenster (start/end) bleiben Anteile der GESAMTEN Scrollstrecke */
    overlays.forEach((el) => {
        const startFraction = parseFloat(el.dataset.start || "0")
        const start = startFraction * totalDur
        const end = parseFloat(el.dataset.end || "1") * totalDur
        const F = OVERLAY_FADE * totalDur
        const xShift = el.dataset.pos === "center" ? -50 : 0
        if (startFraction <= 0) {
            // Start bei 0: sofort sichtbar, ohne Einscroll-Fade
            gsap.set(el, { autoAlpha: 1, y: 0, xPercent: xShift })
        } else {
            gsap.set(el, { autoAlpha: 0, xPercent: xShift })
            timeline.fromTo(el,
                { autoAlpha: 0, y: 26 },
                { autoAlpha: 1, y: 0, duration: F },
                start)
        }
        if (end <= totalDur) {
            timeline.to(el, { autoAlpha: 0, y: -26, duration: F }, end - F)
        }
    })

    let hintHidden = false
    ScrollTrigger.create({
        trigger: stage,
        start: "top top",
        // Pin-Distanz = Film-Strecke + 70vh pro Pin-Inhalt
        end: "+=" + Math.round(totalDur) + "%",
        pin: true,
        // Dieser Pin fügt PIN_DISTANCE an Scroll-Spacing ein und sitzt weit oben
        // auf der Seite. Er wird aber (init-Reihenfolge in main.js) als letzter
        // ScrollTrigger erzeugt. Ohne höhere refreshPriority refreshen die weiter
        // unten liegenden Trigger (z. B. section-text-on-image reveal) VOR diesem
        // Pin und berechnen ihre start/end ohne das Pin-Spacing → sie feuern ~PIN_DISTANCE
        // zu früh (Animation ist durch, bevor die Section im Bild ist). Höhere
        // Priorität lässt den Pin zuerst refreshen, dann stimmen die Positionen darunter.
        refreshPriority: 1,
        scrub: reduceMotion ? true : SCRUB,
        animation: timeline,
        invalidateOnRefresh: true,
        onUpdate(self) {
            if (!hint) return
            if (!hintHidden && self.progress > 0.015) { hint.style.opacity = "0"; hintHidden = true }
            else if (hintHidden && self.progress <= 0.015) { hint.style.opacity = "1"; hintHidden = false }
        },
    })

    // Frames erst laden, wenn die Seite steht UND der Browser idle ist — so
    // bekommen Poster (LCP) und nachladendes Tracking die Leitung zuerst.
    // Scrollt der User früher, starten wir sofort (der Grob-Pass ist in <1s
    // scrubbereit), damit der Film nicht zu spät bereit ist.
    let pumped = false
    const beginLoading = () => {
        if (pumped) return
        pumped = true
        window.removeEventListener("scroll", beginLoading)
        if (frameSet === "2560/") {
            // Fallback-Probe: Filme ohne 2560er-Ordner (nur frames/1920/
            // hochgeladen) fallen nach EINEM Test-Request aufs 1920er zurück
            const probe = new Image()
            probe.onload = pump
            probe.onerror = () => { frameSet = "1920/"; pump() }
            probe.src = srcFor(0)
        } else {
            pump()
        }
    }
    const startWhenIdle = () => {
        if ("requestIdleCallback" in window) requestIdleCallback(beginLoading, { timeout: 1500 })
        else setTimeout(beginLoading, 500)
    }
    window.addEventListener("scroll", beginLoading, { once: true, passive: true })
    if (document.readyState === "complete") startWhenIdle()
    else window.addEventListener("load", startWhenIdle, { once: true })

    // ResizeObserver auf dem Canvas statt window.resize: reagiert auch auf
    // Layout-Änderungen ohne Fensterresize und läuft synchron nach dem Layout,
    // sodass der Backing-Store aktualisiert wird, bevor der gestauchte Bitmap
    // sichtbar wird.
    if (typeof ResizeObserver !== "undefined") {
        const ro = new ResizeObserver(onResize)
        ro.observe(canvas)
    } else {
        window.addEventListener("resize", onResize)
    }

    // Debug-Readout (Taste D) nur im Dev-Build
    if (import.meta.env.DEV && readout) {
        window.addEventListener("keydown", (e) => {
            if ((e.key === "d" || e.key === "D") && e.target === document.body) {
                readout.style.display = readout.style.display === "block" ? "none" : "block"
            }
        })
    }
}

export const init = () => {
    gsap.utils.toArray(".section-scroll-stage").forEach(createScrollStage)
}

export default init
